import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import * as XLSX from "xlsx";
import AgGridTable from "../../components/table/AgGridTable";
import WhiteTooltip from "../../components/WhiteTooltip";
import checkMarkIcon from "../../assets/checkMark.svg";
import unCheckMarkIcon from "../../assets/unCheckMark.svg";
import DoTPidDrilldownDrawer from "./DoTPidDrilldownDrawer";
import DoTEditDrawer, { DotEditSavePayload } from "./DoTEditDrawer";
import DoTHistoryDrawer from "./DoTHistoryDrawer";
import {
  fetchDotControls,
  fetchDotHistory,
  fetchDotSettings,
  fetchDotSummary,
  saveDotControl,
  saveDotSettings,
} from "./dotComplianceApi";
import { computeSummary, isGap, isStale } from "./dotUtils";
import type {
  DotControl,
  DotHistoryEntry,
  DotSettings,
  DotStatus,
  DotSummary,
} from "./types";
import { getDotSourceLabel } from "./types";
import { getSummaryAccentTheme } from "./dotSummaryTheme";
import { toast } from "react-toastify";

function complianceExportLabel(c: DotControl): string {
  if (c.na) return "NA";
  const result = getComplianceResult(c);
  if (result === "Yes") return "Pass";
  if (result === "No") return "Fail";
  return "Not set";
}

function statusExportLabel(status: DotStatus): string {
  if (status === "Yes") return "Yes";
  if (status === "No") return "No";
  return "—";
}

function buildDotExportRows(controls: DotControl[], staleDays: number) {
  return controls.map((c) => {
    const section = `${c.section || ""}${
      c.section_heading ? ` ${c.section_heading}` : ""
    }`.trim();
    const updated = c.updated_at ? new Date(c.updated_at) : null;
    const updatedOn =
      updated && !Number.isNaN(updated.getTime())
        ? updated.toLocaleString()
        : "";
    return {
      "Control ID": c.id,
      Section: section || "—",
      Name: c.title || c.id,
      Description: (c.description || "").trim() || "—",
      Source: getDotSourceLabel(c),
      Compliance: complianceExportLabel(c),
      "Auto Status": statusExportLabel(c.auto_status),
      Override: c.override ? "Yes" : "No",
      "Override Reason": (c.override_reason || "").trim(),
      Comments: (c.comments || "").trim(),
      Gap: isGap(c) ? "Yes" : "No",
      Stale: isStale(c, staleDays) ? "Yes" : "No",
      NA: c.na ? "Yes" : "No",
      "Updated On": updatedOn || "—",
      "Updated By": (c.updated_by || "").trim() || "—",
    };
  });
}

type FilterKey =
  | "all"
  | "gaps"
  | "stale"
  | "unset"
  | "overridden"
  | "spark"
  | "manual"
  | "third_party";

function matchesSourceFilter(c: DotControl, key: FilterKey): boolean {
  if (key === "spark") return c.pid_mapped || c.source === "SPARK";
  if (key === "manual") return !c.pid_mapped && c.source === "Manual";
  if (key === "third_party")
    return !c.pid_mapped && c.source === "Third party";
  return true;
}

function getComplianceResult(c: DotControl): DotStatus {
  if (c.na) return null;
  if (c.pid_mapped && !c.override) return c.auto_status;
  return c.status;
}

/** Pass only when Yes and not stale. NA is neither Pass nor Fail. */
function isCompliancePass(control: DotControl, stale: boolean): boolean {
  return getComplianceResult(control) === "Yes" && !stale;
}

function ComplianceStatusCell({
  control,
  stale,
}: {
  control: DotControl;
  stale: boolean;
}) {
  if (control.na) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          NA
        </Typography>
      </Box>
    );
  }
  const pass = isCompliancePass(control, stale);
  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      <img
        src={pass ? checkMarkIcon : unCheckMarkIcon}
        alt={pass ? "Pass" : "Fail"}
        style={{ width: 18, height: 18 }}
      />
    </Box>
  );
}

function sectionIcon(section: string) {
  const key = (section || "").trim();
  if (key.startsWith("3.1")) return BusinessOutlinedIcon;
  if (key.startsWith("3.2")) return MenuBookOutlinedIcon;
  if (key.startsWith("3.3")) return SecurityOutlinedIcon;
  if (key.startsWith("3.4")) return SchoolOutlinedIcon;
  if (key.startsWith("3.5")) return DescriptionOutlinedIcon;
  return FolderOutlinedIcon;
}

type StatTone = {
  label: string;
  value: number;
  filterKey: Exclude<FilterKey, "all">;
  icon: React.ElementType;
  fg: string;
  bg: string;
  border: string;
  iconBg: string;
  pass?: number;
  fail?: number;
  unset?: number;
};

type SourceBucketStats = {
  total: number;
  pass: number;
  fail: number;
  unset: number;
};

function emptySourceBucket(): SourceBucketStats {
  return { total: 0, pass: 0, fail: 0, unset: 0 };
}

/** Small Pass/Fail/Unset donut for source summary cards */
function SourceMixRing({
  pass,
  fail,
  unset,
  size = 56,
  trackColor,
}: {
  pass: number;
  fail: number;
  unset: number;
  size?: number;
  trackColor: string;
}) {
  const total = pass + fail + unset;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;

  const segments =
    total === 0
      ? []
      : [
          { value: pass, color: "#2D8A4E" },
          { value: fail, color: "#D93025" },
          { value: unset, color: "#B4C3D1" },
        ].filter((s) => s.value > 0);

  let offset = 0;
  const passPct = total === 0 ? 0 : Math.round((pass / total) * 100);

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        alignSelf: "center",
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={trackColor}
          strokeWidth={stroke}
        />
        <g transform={`rotate(-90 ${cx} ${cy})`}>
          {segments.map((seg) => {
            const len = (seg.value / total) * c;
            const el = (
              <circle
                key={seg.color}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth={stroke}
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            );
            offset += len;
            return el;
          })}
        </g>
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 800,
            lineHeight: 1,
            color: passPct >= 75 ? "#2D8A4E" : passPct > 0 ? "#55718B" : "#94A3B8",
          }}
        >
          {passPct}%
        </Typography>
      </Box>
    </Box>
  );
}

function bumpSourceBucket(bucket: SourceBucketStats, c: DotControl) {
  bucket.total += 1;
  const result = getComplianceResult(c);
  if (result === "Yes") bucket.pass += 1;
  else if (result === "No") bucket.fail += 1;
  else bucket.unset += 1;
}

const cellAlignCenter = {
  display: "flex",
  alignItems: "center",
} as const;

function formatUpdatedDate(d: Date): string {
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  return `${day} ${mon} ${year}`;
}

function formatUpdatedTime(d: Date): string {
  return d.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function UpdatedOnCell({
  value,
  isDark,
}: {
  value: string | null | undefined;
  isDark: boolean;
}) {
  const d = value ? new Date(value) : null;
  const valid = d != null && !Number.isNaN(d.getTime());

  if (!valid) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          pl: 0.25,
        }}
      >
        <Box
          sx={{
            px: 1.25,
            py: 0.35,
            borderRadius: 1.5,
            bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#F3F4F6",
            color: isDark ? "rgba(255,255,255,0.45)" : "#9CA3AF",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          –
        </Box>
      </Box>
    );
  }

  const dateFg = isDark ? "#BFDBFE" : "#1E3A8A";
  const timeFg = isDark ? "rgba(191,219,254,0.7)" : "#64748B";
  const iconBg = isDark ? "rgba(37,99,235,0.22)" : "#DBEAFE";
  const iconFg = isDark ? "#93C5FD" : "#2563EB";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        height: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          flexShrink: 0,
          bgcolor: iconBg,
          color: iconFg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, lineHeight: 1.2 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: dateFg,
            whiteSpace: "nowrap",
          }}
        >
          {formatUpdatedDate(d)}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.35,
          }}
        >
          <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: timeFg }} />
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 500,
              color: timeFg,
              whiteSpace: "nowrap",
            }}
          >
            {formatUpdatedTime(d)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const DoTResultsPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const storedUserDetails = localStorage.getItem("userDetails");
  const isDotAdmin = useMemo(() => {
    try {
      const parsed = storedUserDetails ? JSON.parse(storedUserDetails) : null;
      const roleName = String(
        parsed?.role_name || parsed?.role || ""
      ).toLowerCase();
      return roleName.includes("admin");
    } catch {
      return false;
    }
  }, [storedUserDetails]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [controls, setControls] = useState<DotControl[]>([]);
  const [summary, setSummary] = useState<DotSummary | null>(null);
  const [settings, setSettings] = useState<DotSettings>({ stale_days: 180 });
  const [history, setHistory] = useState<DotHistoryEntry[]>([]);
  const [pidDrawerOpen, setPidDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DotControl | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [sectionFilter, setSectionFilter] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const tableBlockRef = useRef<HTMLDivElement | null>(null);
  const [staleDaysDraft, setStaleDaysDraft] = useState(180);

  const cardBg = isDark ? "#191C24" : "#fff";
  const cardBorder = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
  const muted = isDark ? "rgba(255,255,255,0.55)" : "#6B7280";

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [c, s, set, h] = await Promise.all([
        fetchDotControls(),
        fetchDotSummary(),
        fetchDotSettings(),
        fetchDotHistory(),
      ]);
      setControls(c);
      setSummary(s);
      setSettings(set);
      setStaleDaysDraft(set.stale_days);
      setHistory(h);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to load DoT compliance";
      setLoadError(msg);
      setControls([]);
      setSummary(null);
      setHistory([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const liveSummary = useMemo(
    () => summary || computeSummary(controls, settings),
    [summary, controls, settings]
  );

  /** SPARK includes PID-mapped; Manual / Third party from source; Pass/Fail/Unset per bucket */
  const sourceCounts = useMemo(() => {
    const spark = emptySourceBucket();
    const manual = emptySourceBucket();
    const thirdParty = emptySourceBucket();
    for (const c of controls) {
      if (c.pid_mapped || c.source === "SPARK") bumpSourceBucket(spark, c);
      else if (c.source === "Manual") bumpSourceBucket(manual, c);
      else if (c.source === "Third party") bumpSourceBucket(thirdParty, c);
    }
    return { spark, manual, thirdParty };
  }, [controls]);

  const sectionBreakdown = useMemo(() => {
    const map = new Map<
      string,
      { section: string; heading: string; total: number; yes: number }
    >();
    for (const c of controls) {
      if (c.na) continue; // NA excluded from section score
      const key = c.section || "Other";
      const cur = map.get(key) || {
        section: key,
        heading: c.section_heading || key,
        total: 0,
        yes: 0,
      };
      cur.total += 1;
      if (getComplianceResult(c) === "Yes") cur.yes += 1;
      map.set(key, cur);
    }
    return Array.from(map.values())
      .map((s) => ({
        ...s,
        score: s.total === 0 ? 0 : Math.round((s.yes / s.total) * 1000) / 10,
      }))
      .sort((a, b) =>
        a.section.localeCompare(b.section, undefined, { numeric: true })
      );
  }, [controls]);

  const sectionScopedControls = useMemo(() => {
    if (!sectionFilter) return controls;
    return controls.filter((c) => (c.section || "Other") === sectionFilter);
  }, [controls, sectionFilter]);

  const filterCounts = useMemo(() => {
    let gaps = 0;
    let stale = 0;
    let unset = 0;
    for (const c of sectionScopedControls) {
      if (isGap(c)) gaps += 1;
      if (isStale(c, settings.stale_days)) stale += 1;
      if (getComplianceResult(c) == null) unset += 1;
    }
    return { all: sectionScopedControls.length, gaps, stale, unset };
  }, [sectionScopedControls, settings.stale_days]);

  const selectedSectionMeta = useMemo(() => {
    if (!sectionFilter) return null;
    return (
      sectionBreakdown.find((s) => s.section === sectionFilter) || {
        section: sectionFilter,
        heading: "",
      }
    );
  }, [sectionBreakdown, sectionFilter]);

  const filteredControls = useMemo(() => {
    return sectionScopedControls.filter((c) => {
      if (filter === "gaps" && !isGap(c)) return false;
      if (filter === "stale" && !isStale(c, settings.stale_days)) return false;
      if (filter === "unset" && getComplianceResult(c) != null) return false;
      if (filter === "overridden" && !c.override) return false;
      if (
        (filter === "spark" ||
          filter === "manual" ||
          filter === "third_party") &&
        !matchesSourceFilter(c, filter)
      ) {
        return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = `${c.id} ${c.title} ${c.description}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sectionScopedControls, filter, search, settings.stale_days]);

  const toggleSummaryFilter = useCallback((key: FilterKey) => {
    setFilter((prev) => (prev === key ? "all" : key));
  }, []);

  const handleExportExcel = useCallback(() => {
    if (!filteredControls.length) {
      toast.error("No data available to export.");
      return;
    }
    try {
      const exportData = buildDotExportRows(
        filteredControls,
        settings.stale_days
      );
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      worksheet["!cols"] = [
        { wch: 12 },
        { wch: 28 },
        { wch: 42 },
        { wch: 48 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 10 },
        { wch: 28 },
        { wch: 36 },
        { wch: 8 },
        { wch: 8 },
        { wch: 8 },
        { wch: 22 },
        { wch: 24 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "DoT Compliance");
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `DoT_Compliance_${stamp}.xlsx`);
      toast.success(`Exported ${exportData.length} control(s)`);
    } catch (err) {
      console.error("DoT Excel export failed:", err);
      toast.error("Failed to export Excel report.");
    }
  }, [filteredControls, settings.stale_days]);

  const tableRows = useMemo(
    () =>
      filteredControls.map((c) => ({
        ...c,
        controlId: c.id,
        name: c.title || c.id,
        sectionLabel: `${c.section || ""}${
          c.section_heading ? ` ${c.section_heading}` : ""
        }`.trim(),
        sourceLabel: getDotSourceLabel(c),
        updatedByLabel: (c.updated_by || "").trim(),
        recommendations: c.linked_pids?.length || 0,
        isGap: isGap(c),
        isStale: isStale(c, settings.stale_days),
        isUnset: !c.na && getComplianceResult(c) == null,
      })),
    [filteredControls, settings.stale_days]
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Control ID",
        field: "controlId",
        filter: true,
        sortable: true,
        minWidth: 90,
        maxWidth: 110,
        width: 100,
        cellStyle: cellAlignCenter,
        cellRenderer: (params: ICellRendererParams) => {
          const value = params.value || "";
          return (
            <WhiteTooltip title={value}>
              <span style={{ width: "100%" }}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    width: "100%",
                    fontWeight: "bold",
                  }}
                >
                  {value}
                </Typography>
              </span>
            </WhiteTooltip>
          );
        },
      },
      {
        headerName: "Section",
        field: "sectionLabel",
        filter: true,
        sortable: true,
        minWidth: 160,
        maxWidth: 220,
        width: 180,
        cellStyle: cellAlignCenter,
        cellRenderer: (params: ICellRendererParams) => {
          const value = String(params.value || "");
          return (
            <WhiteTooltip title={value}>
              <span style={{ width: "100%" }}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    width: "100%",
                    fontWeight: 600,
                  }}
                >
                  {value || "—"}
                </Typography>
              </span>
            </WhiteTooltip>
          );
        },
      },
      {
        headerName: "Name",
        field: "name",
        filter: true,
        sortable: true,
        flex: 1,
        minWidth: 280,
        suppressSizeToFit: true,
        cellStyle: {
          ...cellAlignCenter,
          overflow: "hidden",
        },
        cellClass: "dot-name-cell",
        cellRenderer: (params: ICellRendererParams) => {
          const row = params.data as DotControl & {
            isGap?: boolean;
            isStale?: boolean;
            isUnset?: boolean;
          };
          const fullName = String(params.value || "");
          return (
            <WhiteTooltip title={fullName}>
              <Box
                className="dot-name-inner"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  className="dot-name-text"
                  sx={{
                    display: "block",
                    flex: "1 1 0%",
                    width: 0,
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    lineHeight: 1.4,
                  }}
                >
                  {fullName}
                </Typography>
                <Box
                  className="dot-name-tags"
                  sx={{
                    width: 108,
                    minWidth: 108,
                    maxWidth: 108,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    pl: 0.75,
                    boxSizing: "border-box",
                  }}
                >
                  {row?.isGap ? (
                    <Chip
                      size="small"
                      label="Gap"
                      className="dot-name-tag"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: "rgba(217,119,6,0.12)",
                        color: "#D97706",
                      }}
                    />
                  ) : null}
                  {row?.isUnset ? (
                    <Chip
                      size="small"
                      label="Not set"
                      className="dot-name-tag"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: isDark
                          ? "rgba(59,130,246,0.18)"
                          : "rgba(59,130,246,0.12)",
                        color: isDark ? "#93C5FD" : "#2563EB",
                      }}
                    />
                  ) : null}
                  {row?.isStale ? (
                    <Chip
                      size="small"
                      label="Stale"
                      className="dot-name-tag"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: "rgba(124,58,237,0.12)",
                        color: "#7C3AED",
                      }}
                    />
                  ) : null}
                  {row?.override ? (
                    <Chip
                      size="small"
                      label="Overridden"
                      className="dot-name-tag"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: isDark
                          ? "rgba(22,163,74,0.18)"
                          : "rgba(22,163,74,0.12)",
                        color: isDark ? "#86EFAC" : "#15803D",
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
            </WhiteTooltip>
          );
        },
      },
      {
        headerName: "Source",
        field: "sourceLabel",
        filter: true,
        sortable: true,
        minWidth: 110,
        maxWidth: 140,
        width: 120,
        cellStyle: cellAlignCenter,
        cellRenderer: (params: ICellRendererParams) => {
          const value = params.value || "—";
          return (
            <WhiteTooltip title={value}>
              <span style={{ width: "100%" }}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    width: "100%",
                    fontWeight: 600,
                  }}
                >
                  {value}
                </Typography>
              </span>
            </WhiteTooltip>
          );
        },
      },
      {
        headerName: "Compliance",
        field: "status",
        filter: false,
        sortable: true,
        minWidth: 110,
        maxWidth: 130,
        width: 120,
        cellStyle: cellAlignCenter,
        cellRenderer: (params: ICellRendererParams) =>
          params.data ? (
            <ComplianceStatusCell
              control={params.data as DotControl}
              stale={Boolean((params.data as { isStale?: boolean }).isStale)}
            />
          ) : null,
      },
      {
        headerName: "Updated On",
        field: "updated_at",
        filter: false,
        sortable: true,
        minWidth: 180,
        maxWidth: 220,
        width: 190,
        cellStyle: cellAlignCenter,
        headerComponent: () => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              height: "100%",
              color: isDark ? "#93C5FD" : "#1E3A8A",
            }}
          >
            <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.2,
                textTransform: "uppercase",
              }}
            >
              Updated On
            </Typography>
          </Box>
        ),
        comparator: (a: string | null, b: string | null) => {
          const ta = a ? new Date(a).getTime() : 0;
          const tb = b ? new Date(b).getTime() : 0;
          const na = Number.isNaN(ta) ? 0 : ta;
          const nb = Number.isNaN(tb) ? 0 : tb;
          return na - nb;
        },
        cellRenderer: (params: ICellRendererParams) => (
          <UpdatedOnCell
            value={params.value as string | null | undefined}
            isDark={isDark}
          />
        ),
      },
      {
        headerName: "Updated By",
        field: "updatedByLabel",
        colId: "updatedBy",
        filter: true,
        sortable: true,
        flex: 0,
        minWidth: 140,
        maxWidth: 200,
        width: 160,
        suppressSizeToFit: true,
        pinned: "right",
        cellStyle: cellAlignCenter,
        cellRenderer: (params: ICellRendererParams) => {
          const value = String(params.value || "").trim();
          return (
            <WhiteTooltip title={value || ""}>
              <span style={{ width: "100%" }}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                    width: "100%",
                    fontWeight: 600,
                  }}
                >
                  {value || "—"}
                </Typography>
              </span>
            </WhiteTooltip>
          );
        },
      },
    ],
    [isDark]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ px: { xs: 1, md: 0 }, py: 4, textAlign: "center" }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
        <Button variant="contained" onClick={() => loadAll()}>
          Retry
        </Button>
      </Box>
    );
  }

  const score = liveSummary.score;
  /** Flip USE_SPARK_SUMMARY_THEME in dotSummaryTheme.ts to restore pastels */
  const accents = getSummaryAccentTheme(isDark);
  const sourceCardThemes = accents.source;

  const sourceCards: (StatTone & {
    labelFg: string;
    wave: string;
  })[] = [
    {
      label: "SPARK",
      value: sourceCounts.spark.total,
      pass: sourceCounts.spark.pass,
      fail: sourceCounts.spark.fail,
      unset: sourceCounts.spark.unset,
      filterKey: "spark",
      icon: LayersOutlinedIcon,
      fg: sourceCardThemes.spark.fg,
      labelFg: sourceCardThemes.spark.labelFg,
      bg: sourceCardThemes.spark.bg,
      border: sourceCardThemes.spark.border,
      iconBg: sourceCardThemes.spark.iconBg,
      wave: sourceCardThemes.spark.wave,
    },
    {
      label: "Manual",
      value: sourceCounts.manual.total,
      pass: sourceCounts.manual.pass,
      fail: sourceCounts.manual.fail,
      unset: sourceCounts.manual.unset,
      filterKey: "manual",
      icon: EditNoteRoundedIcon,
      fg: sourceCardThemes.manual.fg,
      labelFg: sourceCardThemes.manual.labelFg,
      bg: sourceCardThemes.manual.bg,
      border: sourceCardThemes.manual.border,
      iconBg: sourceCardThemes.manual.iconBg,
      wave: sourceCardThemes.manual.wave,
    },
    {
      label: "Third party",
      value: sourceCounts.thirdParty.total,
      pass: sourceCounts.thirdParty.pass,
      fail: sourceCounts.thirdParty.fail,
      unset: sourceCounts.thirdParty.unset,
      filterKey: "third_party",
      icon: GroupsOutlinedIcon,
      fg: sourceCardThemes.third.fg,
      labelFg: sourceCardThemes.third.labelFg,
      bg: sourceCardThemes.third.bg,
      border: sourceCardThemes.third.border,
      iconBg: sourceCardThemes.third.iconBg,
      wave: sourceCardThemes.third.wave,
    },
  ];

  const statCards: StatTone[] = [
    {
      label: "Gaps",
      value: liveSummary.gaps,
      filterKey: "gaps",
      icon: WarningAmberRoundedIcon,
      ...accents.attention.gaps,
    },
    {
      label: "Stale",
      value: liveSummary.stale,
      filterKey: "stale",
      icon: HourglassEmptyRoundedIcon,
      ...accents.attention.stale,
    },
    {
      label: "Not set",
      value: liveSummary.unset,
      filterKey: "unset",
      icon: HelpOutlineRoundedIcon,
      ...accents.attention.unset,
    },
    {
      label: "Overridden",
      value: liveSummary.overridden ?? 0,
      filterKey: "overridden",
      icon: TuneRoundedIcon,
      ...accents.attention.overridden,
    },
  ];

  const filterChipLabel = (key: FilterKey): string => {
    switch (key) {
      case "spark":
        return "SPARK";
      case "manual":
        return "Manual";
      case "third_party":
        return "Third party";
      case "overridden":
        return "Overridden";
      case "gaps":
        return "Gaps";
      case "stale":
        return "Stale";
      case "unset":
        return "Not set";
      default:
        return "All";
    }
  };

  const headerBtnSx = {
    textTransform: "none" as const,
    fontWeight: 600,
    borderRadius: 2,
    px: 1.5,
    borderColor: cardBorder,
    color: isDark ? "#E5E7EB" : "#374151",
    bgcolor: cardBg,
    "&:hover": {
      borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#F9FAFB",
    },
  };

  const attentionCards = statCards.filter((c) => c.filterKey !== "overridden");
  const panelBg = isDark ? "#191C24" : "#FFFFFF";
  const navy = isDark ? "#E5E7EB" : "#2E2E2E";
  const softBorder = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const scoreRingSize = Math.round(118 * 1.15); // ~136

  const panelSx = {
    p: 2,
    height: "100%",
    borderRadius: 2,
    border: `1px solid ${softBorder}`,
    bgcolor: panelBg,
    boxShadow: "none",
    boxSizing: "border-box" as const,
    position: "relative" as const,
    overflow: "hidden",
  };

  return (
    <Box
      sx={{
        px: { xs: 1, md: 0 },
        pb: 2,
        bgcolor: isDark ? "#191C24" : "inherit",
      }}
    >
      {/* Compliance Summary — SPARK dashboard colors via USE_SPARK_SUMMARY_THEME */}
      <Box
        sx={{
          mb: 2.5,
          borderRadius: 2.5,
          border: `1px solid ${softBorder}`,
          bgcolor: panelBg,
          overflow: "hidden",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            borderBottom: summaryOpen ? `1px solid ${softBorder}` : "none",
            bgcolor: panelBg,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              userSelect: "none",
              minWidth: 0,
            }}
            onClick={() => setSummaryOpen((v) => !v)}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: navy,
                  lineHeight: 1.25,
                }}
              >
                Summary
              </Typography>
            </Box>
            <IconButton size="small" sx={{ color: muted, ml: 0.5 }}>
              {summaryOpen ? (
                <KeyboardArrowUpRoundedIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={handleExportExcel}
              sx={headerBtnSx}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryIcon sx={{ fontSize: 18 }} />}
              onClick={() => setHistoryOpen(true)}
              sx={headerBtnSx}
            >
              History
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                setStaleDaysDraft(settings.stale_days);
                setSettingsOpen(true);
              }}
              sx={headerBtnSx}
            >
              Settings
            </Button>
          </Box>
        </Box>

        <Collapse in={summaryOpen} timeout="auto">
          <Box sx={{ p: 2, bgcolor: isDark ? "#191C24" : "#FAFBFC" }}>
            <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    ...panelSx,
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    minHeight: 176,
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 0 42%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      pl: 1,
                      pr: 0.5,
                      zIndex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: scoreRingSize,
                        height: scoreRingSize,
                        filter: accents.scoreShadow,
                        transform: "translateX(-4px)",
                      }}
                    >
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={scoreRingSize}
                        thickness={4.8}
                        sx={{ color: accents.scoreTrack }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={Math.min(100, score)}
                        size={scoreRingSize}
                        thickness={4.8}
                        sx={{
                          color: accents.scoreRing,
                          position: "absolute",
                          left: 0,
                          top: 0,
                          "& .MuiCircularProgress-circle": {
                            strokeLinecap: "round",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 28,
                            fontWeight: 800,
                            lineHeight: 1.1,
                            color: navy,
                          }}
                        >
                          {score}%
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: isDark ? muted : accents.scoreRing,
                            fontWeight: 700,
                          }}
                        >
                          Compliance
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: "1px",
                      alignSelf: "stretch",
                      my: 2.25,
                      ml: -0.5,
                      background: accents.divider,
                      flexShrink: 0,
                      zIndex: 1,
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 0.85,
                      pl: 1.5,
                      pr: 1.75,
                      zIndex: 1,
                    }}
                  >
                    {(
                      [
                        {
                          label: "Compliant",
                          value: liveSummary.yes,
                          color: accents.compliant,
                          chip: accents.compliantChip,
                        },
                        {
                          label: "Non-compliant",
                          value: liveSummary.no,
                          color: accents.nonCompliant,
                          chip: accents.nonCompliantChip,
                        },
                        {
                          label: "Not set",
                          value: liveSummary.unset,
                          color: accents.notSet,
                          chip: accents.notSetChip,
                        },
                        {
                          label: "NA",
                          value: liveSummary.na ?? 0,
                          color: accents.na,
                          chip: accents.naChip,
                        },
                      ] as const
                    ).map((row) => (
                      <Box
                        key={row.label}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1,
                          py: 0.55,
                          borderRadius: 1.5,
                          bgcolor: row.chip,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: row.color,
                            flexShrink: 0,
                            boxShadow: `0 0 0 3px ${row.color}22`,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: isDark ? "rgba(255,255,255,0.75)" : "#334155",
                            flex: 1,
                            fontWeight: 600,
                          }}
                        >
                          {row.label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 800, color: row.color }}
                        >
                          {row.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={panelSx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: accents.accentBlue,
                        boxShadow: `0 0 0 4px ${accents.accentBlue}2E`,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 800, color: navy }}
                    >
                      Compliance by Source
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: 1.25,
                    }}
                  >
                    {sourceCards.map((item) => {
                      const Icon = item.icon;
                      const selected = filter === item.filterKey;
                      const pass = item.pass ?? 0;
                      const fail = item.fail ?? 0;
                      const unset = item.unset ?? 0;
                      const mixTip = `${pass} Pass · ${fail} Fail · ${unset} Unset`;
                      return (
                        <Box
                          key={item.label}
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleSummaryFilter(item.filterKey)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleSummaryFilter(item.filterKey);
                            }
                          }}
                          sx={{
                            position: "relative",
                            px: 1.25,
                            py: 1.2,
                            borderRadius: 2.5,
                            border: `1px solid ${
                              selected ? accents.selectedBorder : item.border
                            }`,
                            bgcolor: item.bg,
                            backgroundImage: item.wave,
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            minHeight: 118,
                            overflow: "hidden",
                            cursor: "pointer",
                            outline: "none",
                            boxShadow: selected
                              ? accents.selectedShadow
                              : isDark
                                ? "none"
                                : "0 6px 16px rgba(85,113,139,0.06)",
                            transition:
                              "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              borderColor: isDark
                                ? "rgba(59,130,246,0.75)"
                                : "#3B82F6",
                              boxShadow: isDark
                                ? "0 0 0 2px rgba(59,130,246,0.28)"
                                : "0 0 0 2px rgba(59,130,246,0.18), 0 8px 18px rgba(37,99,235,0.12)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              zIndex: 1,
                              alignSelf: "stretch",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 1.5,
                                bgcolor: item.iconBg,
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: `0 4px 10px ${item.iconBg}55`,
                              }}
                            >
                              <Icon sx={{ fontSize: 16 }} />
                            </Box>
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: 22,
                                  fontWeight: 800,
                                  color: item.fg,
                                  lineHeight: 1.05,
                                }}
                              >
                                {item.value}
                              </Typography>
                              <Typography
                                noWrap
                                sx={{
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  color: item.labelFg,
                                  mt: 0.2,
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              width: "1px",
                              alignSelf: "stretch",
                              my: 0.75,
                              bgcolor: isDark
                                ? "rgba(255,255,255,0.12)"
                                : "rgba(0,0,0,0.08)",
                              zIndex: 1,
                            }}
                          />
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 1,
                            }}
                          >
                            <WhiteTooltip title={mixTip}>
                              <Box>
                                <SourceMixRing
                                  pass={pass}
                                  fail={fail}
                                  unset={unset}
                                  size={56}
                                  trackColor={
                                    isDark
                                      ? "rgba(255,255,255,0.1)"
                                      : "#D5DEE7"
                                  }
                                />
                              </Box>
                            </WhiteTooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box sx={panelSx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: accents.accentOrange,
                        boxShadow: `0 0 0 4px ${accents.accentOrange}33`,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 800, color: navy }}
                    >
                      Needs Attention
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: 1,
                    }}
                  >
                    {attentionCards.map((item) => {
                      const Icon = item.icon;
                      const selected = filter === item.filterKey;
                      return (
                        <Box
                          key={item.label}
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleSummaryFilter(item.filterKey)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleSummaryFilter(item.filterKey);
                            }
                          }}
                          sx={{
                            position: "relative",
                            px: 1,
                            py: 1.25,
                            borderRadius: 2.5,
                            border: `1px solid ${
                              selected ? accents.selectedBorder : item.border
                            }`,
                            bgcolor: item.bg,
                            backgroundImage: `radial-gradient(ellipse 120% 90% at 50% 120%, ${item.fg}33, transparent 55%)`,
                            textAlign: "center",
                            cursor: "pointer",
                            outline: "none",
                            minHeight: 118,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.75,
                            overflow: "hidden",
                            boxShadow: selected
                              ? accents.selectedShadow
                              : isDark
                                ? "none"
                                : "0 6px 14px rgba(85,113,139,0.06)",
                            transition:
                              "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              borderColor: isDark
                                ? "rgba(59,130,246,0.75)"
                                : "#3B82F6",
                              boxShadow: isDark
                                ? "0 0 0 2px rgba(59,130,246,0.28)"
                                : "0 0 0 2px rgba(59,130,246,0.18), 0 8px 18px rgba(37,99,235,0.12)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              bgcolor: item.iconBg,
                              color: item.fg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: `0 4px 12px ${item.fg}33`,
                            }}
                          >
                            <Icon sx={{ fontSize: 18 }} />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 22,
                              fontWeight: 800,
                              color: item.fg,
                              lineHeight: 1,
                            }}
                          >
                            {item.value}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: item.fg,
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ ...panelSx, p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: accents.accentIndigo,
                    boxShadow: `0 0 0 4px ${accents.accentIndigo}2E`,
                  }}
                />
                <Typography
                  sx={{ fontSize: 13, fontWeight: 800, color: navy }}
                >
                  Compliance by Section
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(5, minmax(0, 1fr))",
                  },
                  gap: 1.25,
                }}
              >
                {sectionBreakdown.map((s) => {
                  const Icon = sectionIcon(s.section);
                  const selected = sectionFilter === s.section;
                  const pct = Math.round(s.score);
                  const ringColor = accents.scoreRing;
                  const title = `${s.section} ${s.heading}`.trim();
                  const sectionWash = isDark
                    ? "radial-gradient(ellipse 90% 80% at 0% 100%, rgba(180,195,209,0.28), transparent 55%)"
                    : "radial-gradient(ellipse 90% 80% at 0% 100%, rgba(85,113,139,0.16), transparent 55%), radial-gradient(ellipse 80% 70% at 100% 0%, rgba(180,195,209,0.45), transparent 50%)";
                  return (
                    <Box
                      key={s.section}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        setSectionFilter((prev) =>
                          prev === s.section ? null : s.section
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSectionFilter((prev) =>
                            prev === s.section ? null : s.section
                          );
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        px: 1.4,
                        py: 1.25,
                        minHeight: 88,
                        borderRadius: 2.75,
                        cursor: "pointer",
                        outline: "none",
                        bgcolor: selected
                          ? accents.selectedSectionBg
                          : isDark
                            ? "rgba(255,255,255,0.04)"
                            : "#FFFFFF",
                        backgroundImage: selected ? undefined : sectionWash,
                        border: `1.5px solid ${
                          selected ? accents.selectedBorder : softBorder
                        }`,
                        boxShadow: selected
                          ? accents.selectedShadow
                          : isDark
                            ? "none"
                            : "0 4px 14px rgba(85,113,139,0.05)",
                        transition:
                          "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
                        "&:hover": {
                          borderColor: isDark
                            ? "rgba(59,130,246,0.75)"
                            : "#3B82F6",
                          boxShadow: isDark
                            ? "0 0 0 2px rgba(59,130,246,0.28)"
                            : "0 0 0 2px rgba(59,130,246,0.18), 0 8px 18px rgba(37,99,235,0.1)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: 48,
                          height: 48,
                          flexShrink: 0,
                          filter: isDark
                            ? "none"
                            : "drop-shadow(0 4px 8px rgba(85,113,139,0.22))",
                        }}
                      >
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={48}
                          thickness={3.8}
                          sx={{ color: accents.scoreTrack }}
                        />
                        <CircularProgress
                          variant="determinate"
                          value={Math.min(100, pct)}
                          size={48}
                          thickness={3.8}
                          sx={{
                            color: ringColor,
                            position: "absolute",
                            left: 0,
                            top: 0,
                            "& .MuiCircularProgress-circle": {
                              strokeLinecap: "round",
                            },
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: selected ? accents.viewDetails : muted,
                          }}
                        >
                          <Icon sx={{ fontSize: 17 }} />
                        </Box>
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 0.75,
                            mb: 0.35,
                          }}
                        >
                          <WhiteTooltip title={title}>
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: navy,
                                lineHeight: 1.3,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: selected
                                    ? accents.viewDetails
                                    : muted,
                                  fontWeight: 800,
                                  mr: 0.4,
                                }}
                              >
                                {s.section}
                              </Box>
                              {s.heading || ""}
                            </Typography>
                          </WhiteTooltip>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 800,
                              color: ringColor,
                              flexShrink: 0,
                            }}
                          >
                            {pct}%
                          </Typography>
                        </Box>
                        <Typography
                          sx={{ fontSize: 11, color: muted, fontWeight: 500 }}
                        >
                          {s.yes}/{s.total} compliant
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Controls table — expands when Summary is collapsed (NIST-style) */}
      <Box
        ref={tableBlockRef}
        sx={{
          borderRadius: "8px",
          border: `1px solid ${cardBorder}`,
          bgcolor: cardBg,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: summaryOpen
            ? "calc(52vh - 24px)"
            : "calc(86vh - 110px)",
          minHeight: summaryOpen ? 320 : 480,
          boxShadow: isDark
            ? "none"
            : "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
        }}
      >
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
            flexShrink: 0,
            borderBottom: `1px solid ${cardBorder}`,
            bgcolor: cardBg,
            zIndex: 2,
          }}
        >
          <TextField
            size="small"
            placeholder="Search controls by ID or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 260,
              flex: "1 1 240px",
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#F9FAFB",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: muted }} />
                </InputAdornment>
              ),
            }}
          />
          {(
            [
              ["all", `All (${filterCounts.all})`],
              ["gaps", `Gaps (${filterCounts.gaps})`],
              ["stale", `Stale (${filterCounts.stale})`],
              ["unset", `Not set (${filterCounts.unset})`],
            ] as const
          ).map(([key, label]) => (
            <Chip
              key={key}
              label={label}
              clickable
              onClick={() => setFilter(key)}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                height: 32,
                bgcolor:
                  filter === key
                    ? isDark
                      ? "rgba(37,99,235,0.25)"
                      : "rgba(37,99,235,0.12)"
                    : isDark
                      ? "rgba(255,255,255,0.06)"
                      : "#F3F4F6",
                color: filter === key ? "#2563EB" : muted,
                border:
                  filter === key
                    ? "1px solid rgba(37,99,235,0.35)"
                    : "1px solid transparent",
              }}
            />
          ))}
          {filter === "spark" ||
          filter === "manual" ||
          filter === "third_party" ||
          filter === "overridden" ? (
            <Chip
              label={filterChipLabel(filter)}
              onDelete={() => setFilter("all")}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                height: 32,
                bgcolor: isDark
                  ? "rgba(37,99,235,0.25)"
                  : "rgba(37,99,235,0.12)",
                color: "#2563EB",
                border: "1px solid rgba(37,99,235,0.35)",
              }}
            />
          ) : null}
          {selectedSectionMeta ? (
            <Chip
              label={`Section: ${selectedSectionMeta.section}${
                selectedSectionMeta.heading
                  ? ` ${selectedSectionMeta.heading}`
                  : ""
              }`}
              onDelete={() => setSectionFilter(null)}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                height: 32,
                maxWidth: 320,
                bgcolor: isDark
                  ? "rgba(37,99,235,0.25)"
                  : "rgba(37,99,235,0.12)",
                color: "#2563EB",
                border: "1px solid rgba(37,99,235,0.35)",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          ) : null}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <AgGridTable
            columnDefs={columnDefs}
            data={tableRows}
            height="100%"
            showSearch={false}
            pagination
            paginationPageSize={50}
            showAppsIcon={false}
            onRowClick={(row: DotControl) => {
              setSelectedRow(row);
              // PID with scanned recs → recommendations (+ inline override); else edit
              if ((row.linked_pids?.length || 0) > 0) {
                setEditDrawerOpen(false);
                setPidDrawerOpen(true);
              } else {
                setPidDrawerOpen(false);
                setEditDrawerOpen(true);
              }
            }}
            gridOptions={{
              defaultColDef: {
                flex: 0,
                resizable: true,
              },
            }}
            styles={{
              padding: "0px",
              width: "100%",
              "& .ag-center-cols-clipper": {
                marginRight: 0,
              },
              "& .ag-center-cols-viewport": {
                marginRight: 0,
              },
              "& .dot-name-cell": {
                overflow: "hidden !important",
                display: "flex !important",
                alignItems: "center",
              },
              "& .dot-name-cell .ag-cell-wrapper": {
                flex: 1,
                width: "100% !important",
                maxWidth: "100% !important",
                minWidth: "0 !important",
                overflow: "hidden !important",
                display: "flex !important",
                alignItems: "center",
              },
              "& .dot-name-cell .ag-cell-value": {
                flex: 1,
                width: "100% !important",
                maxWidth: "100% !important",
                minWidth: "0 !important",
                overflow: "hidden !important",
                display: "block !important",
              },
              "& .dot-name-inner": {
                width: "100% !important",
                maxWidth: "100% !important",
                minWidth: "0 !important",
              },
              "& .dot-name-text": {
                /* flex + width:0 forces equal text slot; ellipsis for overflow */
                flex: "1 1 0% !important",
                width: "0 !important",
                minWidth: "0 !important",
                overflow: "hidden !important",
                textOverflow: "ellipsis !important",
                whiteSpace: "nowrap !important",
              },
              "& .dot-name-tags": {
                width: "108px !important",
                minWidth: "108px !important",
                maxWidth: "108px !important",
                flexShrink: "0 !important",
              },
              "& .dot-name-tag": {
                flexShrink: "0 !important",
              },
            }}
          />
        </Box>
      </Box>

      <DoTPidDrilldownDrawer
        open={pidDrawerOpen}
        control={selectedRow}
        saving={saving}
        canEdit={isDotAdmin}
        onClose={() => {
          setPidDrawerOpen(false);
          setSelectedRow(null);
        }}
        onSave={async (payload: DotEditSavePayload) => {
          if (!isDotAdmin) {
            toast.error("Only Admins can update DoT controls");
            return;
          }
          setSaving(true);
          try {
            const updated = await saveDotControl(payload);
            setControls((prev) =>
              prev.map((c) => (c.id === updated.id ? updated : c))
            );
            setSelectedRow(updated);
            try {
              const [s, h] = await Promise.all([
                fetchDotSummary(),
                fetchDotHistory(),
              ]);
              setSummary(s);
              setHistory(h);
            } catch {
              setSummary(null);
            }
            toast.success(
              payload.reset_to_auto
                ? `Reset ${updated.id} to auto`
                : `Updated ${updated.id}`
            );
          } catch (err) {
            toast.error(
              err instanceof Error ? err.message : "Unable to update control"
            );
          } finally {
            setSaving(false);
          }
        }}
      />

      <DoTEditDrawer
        open={editDrawerOpen}
        control={selectedRow}
        saving={saving}
        canEdit={isDotAdmin}
        onClose={() => {
          setEditDrawerOpen(false);
          setSelectedRow(null);
        }}
        onSave={async (payload: DotEditSavePayload) => {
          if (!isDotAdmin) {
            toast.error("Only Admins can update DoT controls");
            return;
          }
          setSaving(true);
          try {
            const updated = await saveDotControl(payload);
            setControls((prev) =>
              prev.map((c) => (c.id === updated.id ? updated : c))
            );
            try {
              const [s, h] = await Promise.all([
                fetchDotSummary(),
                fetchDotHistory(),
              ]);
              setSummary(s);
              setHistory(h);
            } catch {
              setSummary(null);
            }
            toast.success(
              payload.reset_to_auto
                ? `Reset ${updated.id} to auto`
                : `Updated ${updated.id}`
            );
            setEditDrawerOpen(false);
            setSelectedRow(null);
          } catch (err) {
            toast.error(
              err instanceof Error ? err.message : "Unable to update control"
            );
          } finally {
            setSaving(false);
          }
        }}
      />

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            px: 2.5,
            pt: 2.25,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                bgcolor: isDark ? "rgba(79,70,229,0.22)" : "#EEF2FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SettingsOutlinedIcon sx={{ fontSize: 22, color: "#4F46E5" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>
                DoT Settings
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 13,
                  color: isDark ? "rgba(255,255,255,0.55)" : "#6B7280",
                }}
              >
                Configure the stale state window for DoT controls
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => setSettingsOpen(false)}
            sx={{ color: isDark ? "rgba(255,255,255,0.55)" : "#6B7280" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 0.5, pb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              mb: 0.75,
              color: isDark ? "rgba(255,255,255,0.85)" : "#374151",
            }}
          >
            Stale after (days){" "}
            <Box component="span" sx={{ color: "#DC2626" }}>
              *
            </Box>
          </Typography>
          <TextField
            type="number"
            required
            fullWidth
            size="small"
            value={staleDaysDraft}
            onChange={(e) => setStaleDaysDraft(Number(e.target.value))}
            inputProps={{ min: 1 }}
            disabled={!isDotAdmin}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "#4F46E5",
                },
            }}
          />

          {!isDotAdmin ? (
            <Typography
              sx={{
                mt: 1,
                fontSize: 12.5,
                color: isDark ? "rgba(255,255,255,0.55)" : "#6B7280",
              }}
            >
              Only Admins can update DoT settings.
            </Typography>
          ) : null}

          <Box
            sx={{
              mt: 1.75,
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              p: 1.5,
              borderRadius: 2,
              bgcolor: isDark ? "rgba(79,70,229,0.14)" : "#EEF2FF",
              border: `1px solid ${
                isDark ? "rgba(129,140,248,0.35)" : "#C7D2FE"
              }`,
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 20,
                color: isDark ? "#A5B4FC" : "#4F46E5",
                mt: 0.15,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: isDark ? "#C7D2FE" : "#3730A3",
                  mb: 0.5,
                }}
              >
                What does Stale mean?
              </Typography>
              <Typography
                sx={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: isDark ? "rgba(199,210,254,0.9)" : "#4338CA",
                }}
              >
                A control is marked as Stale when it has not been updated within
                the configured number of days.
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: isDark ? "rgba(199,210,254,0.9)" : "#4338CA",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Example:
                </Box>{" "}
                If the stale period is 1 day, a control that hasn&apos;t been
                updated for more than 1 day is flagged as stale.
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: isDark ? "rgba(199,210,254,0.9)" : "#4338CA",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Action:
                </Box>{" "}
                Review the control and update its information to ensure it
                reflects the latest state.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.25, pt: 0.5, gap: 1 }}>
          <Button
            onClick={() => setSettingsOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              borderColor: isDark ? "rgba(255,255,255,0.2)" : "#D1D5DB",
              color: isDark ? "#E5E7EB" : "#111827",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckRoundedIcon sx={{ fontSize: 18 }} />}
            disabled={!isDotAdmin}
            onClick={async () => {
              if (!isDotAdmin) {
                toast.error("Only Admins can update DoT settings");
                return;
              }
              const days = Number(staleDaysDraft);
              if (!Number.isFinite(days) || days < 1) {
                toast.error("Enter a valid stale period in days");
                return;
              }
              try {
                const next = await saveDotSettings({ stale_days: days });
                setSettings(next);
                try {
                  setSummary(await fetchDotSummary());
                } catch {
                  setSummary(computeSummary(controls, next));
                }
                setSettingsOpen(false);
                toast.success("Stale threshold updated");
              } catch (err) {
                toast.error(
                  err instanceof Error
                    ? err.message
                    : "Unable to update settings"
                );
              }
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: "#4F46E5",
              "&:hover": { bgcolor: "#4338CA" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <DoTHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={history}
        controls={controls}
        onOpenControl={(c) => {
          setHistoryOpen(false);
          setSelectedRow(c);
          if (c.pid_mapped) setPidDrawerOpen(true);
          else setEditDrawerOpen(true);
        }}
      />
    </Box>
  );
};

export default DoTResultsPage;
